import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

const VALIDATION_CONFIG = {
  LONG: { min: 100, max: 500 },
  SHORT: { min: 50, max: null },
  REMARK: { min: 50, max: 500 },
  NUMBER: { min: 1, max: null },
};

const getValidationRules = (data, isRemark = false) => {
  if (!data) return { min: 0, max: null, unitKey: "character" };

  const isNumberType = data.input_validation === "NUMBER";
  const unitKey = isNumberType ? "number" : "character";

  let defaults = VALIDATION_CONFIG.SHORT;
  if (isRemark) defaults = VALIDATION_CONFIG.REMARK;
  else if (data.type === "long") defaults = VALIDATION_CONFIG.LONG;

  const minApi = isRemark
    ? data.minimun_character_remarks
    : data.minimun_character;
  const maxApi = isRemark
    ? data.maximun_character_remarks
    : data.maximun_character;
  const isRequired = isRemark
    ? data.is_required_additional_comment
    : data.is_required;

  let min = typeof minApi === "number" && minApi > 0 ? minApi : defaults.min;

  if (typeof maxApi === "number" && maxApi > 0 && maxApi < min) {
    min = 1;
  } else if (!isRequired && !minApi) {
    min = 1;
  }

  const max = typeof maxApi === "number" && maxApi > 0 ? maxApi : null;

  return { min, max, unitKey };
};

export const useQuestValidation = (
  currentStepData,
  answerText,
  additionalComment,
) => {
  const { t } = useTranslation();

  const validateText = useCallback(
    (text, rules, isRequired) => {
      const cleanText = (text || "").trim();
      const length = cleanText.length;
      const { min, max, unitKey } = rules;

      const isStrictLength = max !== null && min === max;

      const getUnitLabel = (count) =>
        t(`quest.detailMultipleChoice.units.${unitKey}`, { count });

      let isValid = true;
      let errorMessage = null;

      if (length > 0) {
        if (isStrictLength && length !== min) {
          isValid = false;
          errorMessage = t("quest.detailMultipleChoice.validation.exact", {
            count: min,
            unit: getUnitLabel(min),
          });
        } else if (length < min) {
          isValid = false;
          errorMessage = t("quest.detailMultipleChoice.validation.min", {
            count: min,
            unit: getUnitLabel(min),
          });
        } else if (max !== null && length > max) {
          isValid = false;
          errorMessage = t("quest.detailMultipleChoice.validation.max", {
            count: max,
            unit: getUnitLabel(max),
          });
        }
      } else if (isRequired) {
        isValid = false;
      }

      return {
        isValid,
        length,
        min,
        max,
        unitKey,
        isStrictLength,
        errorMessage,
      };
    },
    [t],
  );

  const mainAnswerValidation = useMemo(() => {
    if (!currentStepData) return { isValid: false, length: 0 };
    const rules = getValidationRules(currentStepData, false);
    return validateText(answerText, rules, currentStepData.is_required);
  }, [currentStepData, answerText, validateText]);

  const remarkValidation = useMemo(() => {
    if (!currentStepData?.is_additional_comment)
      return { isValid: true, length: 0 };
    const rules = getValidationRules(currentStepData, true);
    return validateText(
      additionalComment,
      rules,
      currentStepData.is_required_additional_comment,
    );
  }, [currentStepData, additionalComment, validateText]);

  return { mainAnswerValidation, remarkValidation };
};
